import React from 'react';
import { Store } from 'store';
import { ArrowLeft, DownloadIcon } from '@clariness/clarikit-icons';
import { Box, Button } from '@clariness/clarikit-react';
import { Events } from '@clariness/tracking-event-runtime';
import { useIntl } from 'react-intl';
import { useRedirect } from 'utils/routing';
import { Routing } from 'global/routing';
import { ShouldRender } from 'components/ShouldRender';
import { useMultiStep } from 'components/MultiStep';
import { captureEventV2 } from 'utils/events/captureEvent';
import { Services } from 'services';
import { usePatientDetailsContext } from '../../ReferralDetails';

const PROFILE_STEP = 1;

export const PatientDetailsFooter: React.FC = () => {
  const intl = useIntl();
  const { currentReferral, isExternalReferral } = usePatientDetailsContext();
  const { activeStep } = useMultiStep();

  const { openModal } = Store.useModal();
  const { user } = Store.useUser();
  const { getPdf, isLoading } = Services.Referrals.useGetPdf();

  const { redirect } = useRedirect();

  const showExportPdf = activeStep === PROFILE_STEP && Boolean(currentReferral);

  return (
    <Box
      as="footer"
      display="flex"
      alignItems="center"
      justifyContent="space-between"
      width="100%"
      backgroundColor="white"
      paddingX="110px"
      style={{
        flexShrink: 0,
        height: '112px',
        borderTop: '2px solid #D9D9D9',
      }}
    >
      <Button
        size="m"
        variant="tertiary"
        iconLeft={ArrowLeft}
        onClick={() => {
          redirect(Routing.HOME.getPath());
        }}
      >
        {intl.formatMessage({
          id: 'is.patient_details.back_to_patient_list',
          defaultMessage: 'Back to Patient List',
        })}
      </Button>
      <Box display="flex" alignItems="center" gap="24">
        <ShouldRender when={showExportPdf}>
          <Button
            size="m"
            variant="secondary"
            loading={isLoading}
            iconLeft={DownloadIcon}
            onClick={() => {
              if (!currentReferral) {
                return;
              }

              captureEventV2(
                Events.InvestigatorService.InvestigatorExportedPatientProfile({
                  InvestigatorID: user?.id ?? '',
                })
              );
              getPdf({
                id: currentReferral.id,
                firstName: currentReferral.patient.firstName,
                lastName: currentReferral.patient.lastName,
                isExternalReferral,
              });
            }}
          >
            {intl.formatMessage({
              id: 'is.patient_details_modal.footer.export_pdf_button',
              defaultMessage: 'Export as PDF',
            })}
          </Button>
        </ShouldRender>
        <Button
          size="m"
          onClick={() => {
            openModal({
              modalType: 'UpdatePatientStatus',
              modalProps: {
                id: currentReferral?.id || '',
                status: currentReferral?.status,
                appointmentDateTime: currentReferral?.appointmentDateTime,
                referralDate: currentReferral?.referralDate,
                currentConsentedDate: currentReferral?.consentedDate,
                isVisited: currentReferral?.isVisited,
                isExternalReferral,
              },
            });
          }}
        >
          {intl.formatMessage({
            id: 'is.patient_details_modal.footer.update_status_button',
            defaultMessage: 'Update Status',
          })}
        </Button>
      </Box>
    </Box>
  );
};
